import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as workflowService from '../services/workflowService';
import type { Workflow, WorkflowListItem } from '../types/workflow';

// Query keys
export const workflowKeys = {
  all: ['workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  list: (filters: string) => [...workflowKeys.lists(), { filters }] as const,
  details: () => [...workflowKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
};

// 获取工作流列表
export function useWorkflows() {
  return useQuery<WorkflowListItem[], Error>({
    queryKey: workflowKeys.lists(),
    queryFn: workflowService.getWorkflows,
    staleTime: 30 * 1000, // 30秒内数据视为新鲜
    refetchInterval: 60 * 1000, // 每分钟自动刷新
  });
}

// 获取单个工作流
export function useWorkflow(id: string) {
  return useQuery<Workflow, Error>({
    queryKey: workflowKeys.detail(id),
    queryFn: () => workflowService.getWorkflow(id),
    enabled: !!id, // 只有id存在时才执行
    staleTime: 5 * 60 * 1000, // 5分钟内数据视为新鲜
  });
}

// 创建工作流
export function useCreateWorkflow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (workflow: Omit<Workflow, 'id' | 'createdAt' | 'updatedAt'>) =>
      workflowService.createWorkflow(workflow),
    onSuccess: () => {
      // 创建成功后刷新列表
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// 更新工作流
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Workflow> }) =>
      workflowService.updateWorkflow(id, updates),
    onSuccess: (_, variables) => {
      // 更新成功后刷新相关缓存
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// 删除工作流
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: workflowService.deleteWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// 执行工作流
export function useExecuteWorkflow() {
  return useMutation({
    mutationFn: ({ id, variables }: { id: string; variables?: Record<string, unknown> }) =>
      workflowService.executeWorkflow(id, variables),
  });
}
